import React, { useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyGetReviewbyIdQuery } from "../../service/manageReviews";
import { showError } from "../../constants/toasts";
import { PRODUCT_STATUS } from "../../constants/enums";

interface Product {
  name: string;
  images: string[];
}

interface ReviewDetail {
  product: Product;
  message: string;
  createdAt: string;
  status: number;
}

const ReviewsDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [fetchReviewDetail, { data: response, isError, isLoading }] =
    useLazyGetReviewbyIdQuery();

  const reviewDetail = response?.data as ReviewDetail | undefined;

  const fetchReview = useCallback(async () => {
    if (!id) return;

    try {
      await fetchReviewDetail({ id });
    } catch (error) {
      console.error("Failed to fetch review:", error);
      showError("Failed to fetch review details");
    }
  }, [id, fetchReviewDetail]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  const handleNavigation = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const formattedDate = useMemo(() => {
    return reviewDetail?.createdAt
      ? new Date(reviewDetail.createdAt).toLocaleDateString("en-GB")
      : "N/A";
  }, [reviewDetail?.createdAt]);

  if (isLoading) {
    return <div className="loading">Loading review details...</div>;
  }

  if (isError) {
    return (
      <div className="error">
        Error loading review details. Please try again.
      </div>
    );
  }

  if (!reviewDetail) {
    return <div className="not-found">Review not found</div>;
  }

  return (
    <div className="review-detail-container">
      <div className="main_title">
        <h1>Review Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/manage-reviews")}>
            Manage Reviews
          </button>{" "}
          - Review Detail
        </p>
      </div>

      <div className="cards view_page">
        <div className="gap_p">
          <div className="w_100">
            <h6>Product Name</h6>
            <p>{reviewDetail.product?.name || "N/A"}</p>
          </div>

          {reviewDetail.product?.images?.length > 0 && (
            <div className="w_100">
              <h6>Product Images</h6>
              <div
                className="product-images-container"
                style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
              >
                {reviewDetail.product.images.map((image, index) => (
                  <div
                    key={index}
                    className="product-image-wrapper"
                    style={{ marginBottom: "16px" }}
                  >
                    <img
                      src={image}
                      alt={`Product ${index + 1}`}
                      className="product-image"
                      loading="lazy"
                      style={{
                        maxWidth: "200px",
                        height: "auto",
                        borderRadius: "4px",
                        border: "1px solid #eee",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="w_33">
            <h6>Created Date</h6>
            <p>{formattedDate}</p>
          </div>

          <div className="w_100">
            <h6>Feedback Message</h6>
            <p className="feedback-message">
              {reviewDetail.message || "No message provided"}
            </p>
          </div>
          <div className="w_100">
            <h6>Status</h6>
            <p className="feedback-message">
              <b>
                {reviewDetail?.status === PRODUCT_STATUS.APPROVED
                  ? "Approved"
                  : reviewDetail?.status === PRODUCT_STATUS.REJECTED
                  ? "Rejected"
                  : "Pending"}
              </b>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ReviewsDetail);
